/** @jsx jsx */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { css, jsx } from '@compiled/react';
import { cssMap } from '@atlaskit/css';
import { Field } from '@atlaskit/form';
import { Text, Box, Grid } from '@atlaskit/primitives/compiled';

import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import LockLockedIcon from '@atlaskit/icon/core/lock-locked';
import LockUnlockedIcon from '@atlaskit/icon/core/lock-unlocked';
import { token } from '@atlaskit/tokens';
import Select, {
	components,
	type CSSObjectWithLabel,
	type ControlProps,
	type OptionProps,
	type ValueContainerProps,
} from '@atlaskit/select';
import { Skeleton } from '@atlassian/jira-common-components-skeleton/src/styled.tsx';
import { defineMessages, useIntl } from '@atlassian/jira-intl';
import type {
	GetProjectAccessResponse,
	ProjectAccessLevel,
} from '../../services/use-project-access-level/types.tsx';

const gridStyles = cssMap({
	root: {
		gridTemplateColumns: 'auto 1fr',
		gridTemplateAreas: "'icon dropdown' 'blank description'",
	},
});

const FIELD_NAME = 'projectAccessLevel';

const styles = cssMap({
	iconContainer: {
		gridArea: 'icon',
		paddingInline: token('space.050'),
		alignSelf: 'center',
		marginLeft: token('space.negative.050'),
	},
	dropdownContainer: {
		gridArea: 'dropdown',
	},
	descriptionContainer: {
		gridArea: 'description',
		width: 'fit-content',
		marginLeft: token('space.100'),
	},
	optionDescriptionContainer: {
		gridArea: 'description',
		width: 'fit-content',
	},
	field: {
		borderTopColor: token('color.border'),
		borderTopStyle: 'solid',
		borderTopWidth: token('border.width'),
		marginTop: token('space.300'),
		marginBottom: token('space.150'),
		marginLeft: token('space.negative.300'),
		marginRight: token('space.negative.300'),
		paddingTop: token('space.100'),
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
	},
	skeleton: {
		marginBottom: token('space.150'),
	},
});
// `css` is used to customise the styles of the Select components, `cssMap` / `xcss` don't work here
const controlStyles = css({
	width: 'fit-content',
});
const valueContainerStyles = css({
	fontWeight: token('font.weight.bold'),
});

const messages = defineMessages({
	projectAccessLevelLabel: {
		id: 'share-dialog.fields.ProjectAccessLevelField.project-access-level-label.non-final',
		defaultMessage: 'Project access',
		description: 'Label for the project access level field',
	},
});

type ProjectAccessLevelSelectOption = {
	label: ProjectAccessLevel['displayName'];
	value: ProjectAccessLevel['value'];
	iconName: ProjectAccessLevel['iconName'];
	description: ProjectAccessLevel['description'];
};

export type ProjectAccessLevelFieldValues = {
	[FIELD_NAME]: ProjectAccessLevel;
};

const SelectControl = (props: ControlProps<ProjectAccessLevelSelectOption>) => {
	return <components.Control {...props} appearance="subtle" isCompact css={controlStyles} />;
};

const SelectValueContainer = (props: ValueContainerProps<ProjectAccessLevelSelectOption>) => {
	return <components.ValueContainer {...props} css={valueContainerStyles} />;
};

const SelectOption = ({ children, ...props }: OptionProps<ProjectAccessLevelSelectOption>) => {
	const { data } = props;

	if (!data) {
		return null;
	}

	return (
		<components.Option {...props}>
			<Grid xcss={gridStyles.root}>
				<Box xcss={styles.iconContainer}>{getIcon(data.iconName)}</Box>
				<Box xcss={styles.dropdownContainer}>
					<Text weight="bold">{data.label}</Text>
				</Box>
				<Box xcss={styles.optionDescriptionContainer}>
					<Text color="color.text.subtle" size="small">
						{data.description}
					</Text>
				</Box>
			</Grid>
		</components.Option>
	);
};

export const getIcon = (icon?: string) => {
	const localProps = {
		label: '',
		color: token('color.icon.accent.gray'),
		LEGACY_size: 'medium' as const,
		spacing: 'spacious' as const,
	};
	switch (icon) {
		case 'private':
			return <LockLockedIcon {...localProps} />;
		case 'limited':
			return <PeopleGroupIcon {...localProps} />;
		case 'open':
		default:
			return <LockUnlockedIcon {...localProps} />;
	}
};

const transformToSelectOption = (level: ProjectAccessLevel): ProjectAccessLevelSelectOption => ({
	label: level.displayName,
	value: level.value,
	iconName: level.iconName,
	description: level.description,
});

type Props = {
	currentAccessLevel?: ProjectAccessLevel;
	accessLevels?: ProjectAccessLevel[];
	getProjectAccessLevels: () => Promise<GetProjectAccessResponse | undefined>;
	isLoading?: boolean;
};

export const ProjectAccessLevelSelectContainer = ({
	isLoading,
	currentAccessLevel,
	accessLevels,
	getProjectAccessLevels,
}: Props) => {
	useEffect(() => {
		getProjectAccessLevels();
		// only used to fetch the project access levels when the component mounts
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (!accessLevels && isLoading) {
		return (
			<Box xcss={styles.skeleton}>
				<Skeleton height="20px" />
			</Box>
		);
	}

	return (
		<ProjectAccessLevelSelect
			currentAccessLevel={currentAccessLevel}
			accessLevels={accessLevels}
			getProjectAccessLevels={getProjectAccessLevels}
		/>
	);
};

const ProjectAccessLevelSelect = ({ currentAccessLevel, accessLevels }: Props) => {
	const { formatMessage } = useIntl();

	const selectContainerRef = useRef<HTMLDivElement | null>(null);

	const selectOptions = useMemo(
		() => accessLevels?.map((level) => transformToSelectOption(level)),
		[accessLevels],
	);
	const defaultValue = useMemo(
		() => selectOptions?.find((option) => option.value === currentAccessLevel?.value),
		[currentAccessLevel, selectOptions],
	);
	const [currentValue, setCurrentValue] = useState<ProjectAccessLevelSelectOption | undefined>(
		defaultValue,
	);

	return (
		<Box xcss={styles.field}>
			<Field<ProjectAccessLevelSelectOption>
				name={FIELD_NAME}
				label={formatMessage(messages.projectAccessLevelLabel)}
				defaultValue={defaultValue}
			>
				{({ fieldProps: { onChange, ...fieldProps } }) => (
					<Grid xcss={gridStyles.root}>
						<Box xcss={styles.iconContainer} testId="share-button.ui.project-access-level.box">
							{getIcon(currentValue?.iconName)}
						</Box>
						<Box xcss={styles.dropdownContainer} ref={selectContainerRef}>
							<Select<ProjectAccessLevelSelectOption>
								{...fieldProps}
								value={currentValue}
								options={selectOptions}
								onChange={(value: ProjectAccessLevelSelectOption | null) => {
									if (!value) {
										return;
									}

									setCurrentValue(value);
									onChange(value);
								}}
								isSearchable={false}
								menuPortalTarget={globalThis.document.body}
								components={{
									Control: SelectControl,
									ValueContainer: SelectValueContainer,
									Option: SelectOption,
								}}
								// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
								styles={{
									menuPortal: (base: CSSObjectWithLabel) => ({
										...base,
										zIndex: 9999,
										width: selectContainerRef.current?.offsetWidth,
									}),
									dropdownIndicator: (base: CSSObjectWithLabel) => ({
										...base,
										padding: 0,
										marginLeft: token('space.negative.075'),
									}),
								}}
								menuPosition="fixed"
								spacing="compact"
							/>
						</Box>
						<Box xcss={styles.descriptionContainer}>
							<Text color="color.text.subtle" size="small">
								{currentValue?.description}
							</Text>
						</Box>
					</Grid>
				)}
			</Field>
		</Box>
	);
};
